import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DoCheck, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { MessagingService } from "../../../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../../../core/messaging/severity-type.enum";
import { FormService } from "../../../../../../dynamic-forms/form.service";
import { TextboxType } from "../../../../../../dynamic-forms/inputs/textbox/textbox-type.enum";
import { Textbox } from "../../../../../../dynamic-forms/inputs/textbox/textbox.model";
import { NumberHelper } from "../../../../../../utilities/contracts/number-helper";
import { RegExHelper } from "../../../../../../utilities/reg-Ex-Helper";
import { AuditPageEntries } from "../../../../member/chase-detail/chase-detail-chart/audit-page-entry/audit-page-entries.model";
import { AuditPageEntryService } from "../../../../member/chase-detail/chase-detail-chart/audit-page-entry/audit-page-entry.service";
import { RiskPageData } from "../../../../member/chase-detail/chase-detail-chart/audit-page-entry/risk-page-data.model";
import { ProjectType } from "../../../../project/project-type.enum";
import { AuditPackageStatus } from "../../../audit-package-status.enum";
import { ChaseDetailForAuditItem } from "../../../chase-detail-for-audit-item.model";
import { AuditDataSharingService } from "../../../services/audit-data-sharing.service";
import { AuditPackageService } from "../../../services/audit-package.service";
import { AuditQueryGridFiltersService } from "../../audit-query-shared/audit-query-grid-filters/audit-query-grid-filters.service";
import { IAuditQueryGridData } from "../../audit-query-shared/audit-query-grid-filters/member-radv-record.model";
import { AuditQueryGridRiskService } from "../audit-query-grid-risk/audit-query-grid-risk.service";

@Component({
    selector: "app-audit-query-item-user-rank-risk",
    templateUrl: "./audit-query-item-user-rank-risk.component.html",
    styleUrls: ["./audit-query-item-user-rank-risk.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditQueryItemUserRankRiskComponent implements OnInit, OnChanges, DoCheck {
    form: FormGroup;
    @Output() onStatusChange: EventEmitter<any> = new EventEmitter(null);
    @Input() chaseDetailsForAudit: IAuditQueryGridData;
    @Input() parentComponent: any; // Reference to parent grid component
    userRankInput: Textbox;
    private originalUserRank: number;
    private cachedAuditPackageId: number | null = null;

    constructor(
        protected readonly formService: FormService,
        private auditPageEntryService: AuditPageEntryService,
        private auditPackageService: AuditPackageService,
        private auditQueryGridRiskService: AuditQueryGridRiskService,
        private auditQueryGridFiltersService: AuditQueryGridFiltersService,
        private messagingService: MessagingService,
        private changeDetector: ChangeDetectorRef,
        private auditDataSharingService: AuditDataSharingService
    ) { }

    ngOnInit(): void {
        this.userRankInput = new Textbox({
            key: "userRank",
            type: TextboxType.TEXT,
            disabled: !this.isAuditReady(),
            validators: [this.validateUserRank()],
            errorMessages: {
                pattern: "Value must be a positive whole number only",
                invalidUserRank : "Value must not be greater than 20",
            },
        });
        this.form = this.formService.createFormGroup([this.userRankInput]);
        this.originalUserRank = this.chaseDetailsForAudit.userRank;
        this.form.get(this.userRankInput.key).setValue(this.chaseDetailsForAudit.userRank);

        this.changeDetector.markForCheck();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.chaseDetailsForAudit && this.form) {
            this.originalUserRank = this.chaseDetailsForAudit.userRank;
            this.form.get(this.userRankInput.key).setValue(this.chaseDetailsForAudit.userRank);

            const isDisabled = !this.isAuditReady();
            if (isDisabled) {
                this.form.get(this.userRankInput.key).disable();
            } else {
                this.form.get(this.userRankInput.key).enable();
            }

            this.changeDetector.markForCheck();
        }
    }

    ngDoCheck(): void {
        if (this.chaseDetailsForAudit && this.form && this.chaseDetailsForAudit.userRank !== this.originalUserRank) {
            this.originalUserRank = this.chaseDetailsForAudit.userRank;
            this.form.get(this.userRankInput.key).setValue(this.chaseDetailsForAudit.userRank);
            this.changeDetector.markForCheck();
        }
    }


    onUserRankBlur(): void {
        const control = this.form.get("userRank");
        const newUserRank = control.value;

        if (newUserRank !== this.originalUserRank) {
            if (control.valid) {
                this.updateUserRank(newUserRank);
                this.originalUserRank = newUserRank;
            } else {
                const errors = control.errors;
                let errorMessage: string;
                if (errors?.pattern) {
                    errorMessage = "User Rank must be a positive whole number.";
                } else if (errors?.invalidUserRank) {
                    errorMessage = "User Rank must not be greater than 20.";
                }
                this.messagingService.showToast(errorMessage, SeverityType.ERROR);
                control.setValue(this.originalUserRank);
                this.changeDetector.markForCheck();
            }
        }
    }

    private isAuditReady(): boolean {
        return this.chaseDetailsForAudit.auditReady &&
               this.chaseDetailsForAudit.auditReady.toLowerCase() === "yes" && !this.chaseDetailsForAudit.isAuditPackageLocked;
    }


    private updateUserRank(userRank: number): void {
        this.getOrCreateAuditPackage().then(auditPackageId => {
            if (auditPackageId) {
                this.saveUserRankWithAuditPackage(userRank, auditPackageId);
            } else {
                this.messagingService.showToast("Failed to get or create audit package.", SeverityType.ERROR);
                this.changeDetector.markForCheck();
            }
        }).catch(error => {
            this.messagingService.showToast("Failed to get or create audit package.", SeverityType.ERROR);
            this.changeDetector.markForCheck();
        });
    }

    private async getOrCreateAuditPackage(): Promise<number> {
        if (this.cachedAuditPackageId) {
            return this.cachedAuditPackageId;
        }

        const activeAuditPackageId = this.auditDataSharingService.getActiveAuditPackageId();
        if (activeAuditPackageId) {
            this.cachedAuditPackageId = activeAuditPackageId;
            this.chaseDetailsForAudit.auditPackageId = activeAuditPackageId;
            return activeAuditPackageId;
        }

        if (this.parentComponent) {
            const parentAuditPackageId = this.parentComponent.getActiveAuditPackageId();
            if (parentAuditPackageId) {
                this.cachedAuditPackageId = parentAuditPackageId;
                this.chaseDetailsForAudit.auditPackageId = parentAuditPackageId;
                return parentAuditPackageId;
            }
        }

        return new Promise((resolve, reject) => {
            this.auditPackageService.getActiveAuditPackageForMember(
                this.chaseDetailsForAudit.memberId
            ).subscribe(
                activeAuditPackages => {
                    const activeAuditPackage = activeAuditPackages && activeAuditPackages.length > 0 ? activeAuditPackages[0] : null;

                    if (activeAuditPackage && activeAuditPackage.auditPackageID) {
                        this.cachedAuditPackageId = activeAuditPackage.auditPackageID;
                        this.chaseDetailsForAudit.auditPackageId = activeAuditPackage.auditPackageID;
                        resolve(activeAuditPackage.auditPackageID);
                    } else {
                        this.auditDataSharingService.getOrCreateAuditPackageOnce(() => this.createAuditPackage()).then(auditPackageId => {
                            this.cachedAuditPackageId = auditPackageId;
                            resolve(auditPackageId);
                        }).catch(error => {
                            reject(error);
                        });
                    }
                },
                error => {
                    this.auditDataSharingService.getOrCreateAuditPackageOnce(() => this.createAuditPackage()).then(auditPackageId => {
                        this.cachedAuditPackageId = auditPackageId;
                        resolve(auditPackageId);
                    }).catch(createError => {
                        reject(createError);
                    });
                }
            );
        });
    }

    private async createAuditPackage(): Promise<number> {
        return new Promise((resolve, reject) => {
            // Check if entityId is available for Risk projects
            if (!this.chaseDetailsForAudit.entityId) {
                const error = new Error("Entity ID is required for creating audit package in Risk projects");
                this.messagingService.showToast("Entity ID is required for user ranking.", SeverityType.ERROR);
                reject(error);
                return;
            }

            const updateAuditData = new ChaseDetailForAuditItem({
                projectId: this.chaseDetailsForAudit.projectId,
                entityId: this.chaseDetailsForAudit.entityId,
                memberId: this.chaseDetailsForAudit.memberId,
                status: AuditPackageStatus.ACTIVE,
                memberDOB: this.chaseDetailsForAudit.memberDOB,
                memberFirstName: this.chaseDetailsForAudit.memberFirstName,
                memberLastName: this.chaseDetailsForAudit.memberLastName,
            });

            this.auditPackageService.createOrGetAuditPackage(updateAuditData).subscribe(
                auditPackageId => {
                    this.cachedAuditPackageId = auditPackageId;
                    this.chaseDetailsForAudit.auditPackageId = auditPackageId;
                    resolve(auditPackageId);
                },
                error => {
                    this.messagingService.showToast("Failed to create audit package for user ranking.", SeverityType.ERROR);
                    reject(error);
                }
            );
        });
    }

    private saveUserRankWithAuditPackage(userRank: number, auditPackageId: number): void {

        const riskPageData = new RiskPageData({
            entityId: this.chaseDetailsForAudit.entityId,
            userRank,
            icd: this.chaseDetailsForAudit.icdCode || "",
            hcc: this.chaseDetailsForAudit.hccCode,
            vrc: this.chaseDetailsForAudit.validationResultCode || "",
            chaseId: this.chaseDetailsForAudit.chaseId,
            finalRank: this.chaseDetailsForAudit.finalRank,
            sourceTypeName: this.chaseDetailsForAudit.source || "",
            startDate: this.chaseDetailsForAudit.startDate,
            endDate: this.chaseDetailsForAudit.endDate,
            cmsModel: this.chaseDetailsForAudit.cmsModel || "",
            reveleerRank: this.chaseDetailsForAudit.initialRank,
            reportingStatusName: this.chaseDetailsForAudit.reportingStatusName || "",
            encounterType: this.chaseDetailsForAudit.encounterType || "",
            paymentYear: this.chaseDetailsForAudit.paymentYear,
            planId: this.chaseDetailsForAudit.planId || "",
            product: this.chaseDetailsForAudit.product || "",
            hccValidationResult: this.chaseDetailsForAudit.hccValidationResult || "",
        });

        const auditPageEntries = new AuditPageEntries({
            auditPackageItemId: NumberHelper.toNumber(this.chaseDetailsForAudit.auditPackageItemId),
            auditPackageId,
            projectId: this.chaseDetailsForAudit.projectId,
            chaseId: this.chaseDetailsForAudit.chaseId,
            numeratorId: this.chaseDetailsForAudit.numeratorId,
            riskPageData,
            projectType: ProjectType.RISK,
            memberId: this.chaseDetailsForAudit.memberId,
        });

        this.auditPageEntryService.savePageEntriesForRisk(auditPageEntries).subscribe(
            auditPackageItemId => {
                if (auditPackageItemId) {
                    this.chaseDetailsForAudit.auditPackageItemId = auditPackageItemId.toString();
                }
                if (this.chaseDetailsForAudit.memberId) {
                    this.auditQueryGridFiltersService.getMemberRadvCard(this.chaseDetailsForAudit.memberId).subscribe({
                        next: radvCardData => {
                            this.auditQueryGridRiskService.sendMemberRadvCardData(radvCardData);
                            setTimeout(() => {
                                this.auditQueryGridRiskService.reloadAuditGridFromState();
                            },         0);
                            this.messagingService.showToast("User Rank saved successfully.", SeverityType.SUCCESS);
                            this.changeDetector.markForCheck();
                        },
                        error: () => {
                            this.auditQueryGridRiskService.reloadAuditGridFromState();
                            this.messagingService.showToast("User Rank saved successfully.", SeverityType.SUCCESS);
                            this.changeDetector.markForCheck();
                        },
                    });
                } else {
                    this.auditQueryGridRiskService.reloadAuditGridFromState();
                    this.messagingService.showToast("User Rank saved successfully.", SeverityType.SUCCESS);
                    this.changeDetector.markForCheck();
                }
            },
            err => {
                this.messagingService.showToast("Failed to update User Rank.", SeverityType.ERROR);
                this.changeDetector.markForCheck();
            }
        );
    }

    private clearAuditPackageCache(): void {
        this.cachedAuditPackageId = null;
    }

    private validateUserRank(): ValidatorFn {
        const regExHelper = RegExHelper.positiveWholeNumber;
        return (control: AbstractControl): ValidationErrors | null => {
             const value = control.value;
             if (!value) {
                 return null;
             }

             if (!regExHelper.test(value)) {
                 return {pattern : true};
             }

             const numValue = Number(value);
             return numValue > 20 ? { invalidUserRank: true } : null;
         };
    }
}
